class PmCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.cartJsonUrl = this.dataset.cartJsonUrl || '/cart.js';
    this.cartUpdateUrl = this.dataset.cartUpdateUrl || '/cart/update.js';
    this.cartClearUrl = this.dataset.cartClearUrl || '/cart/clear.js';
    this.panel = this.querySelector('[data-pm-cart-panel]');
    this.lines = this.querySelector('[data-pm-cart-lines]');
    this.status = this.querySelector('[data-pm-cart-status]');
    this.footer = this.querySelector('[data-pm-cart-footer]');
    // Bank drawer only (snippets/pm-cart-drawer.liquid renders it with pk_bank).
    this.keepShopping = this.querySelector('[data-pm-cart-keep]');
    this.subtotal = this.querySelector('[data-pm-cart-subtotal]');
    this.savings = this.querySelector('[data-pm-cart-savings]');
    this.shippingCopy = this.querySelector('[data-pm-cart-shipping-copy]');
    this.freeShipping = this.querySelector('[data-pm-cart-free-shipping]');
    try { this.catalog = JSON.parse(this.querySelector('[data-pm-cart-catalog]')?.textContent || '{}'); }
    catch (_) { this.catalog = {}; }
    // Bank-only Pup Club descriptor. Absent on every other drawer, so the upsell
    // cannot render outside the Bank cart.
    try { this.membership = JSON.parse(this.querySelector('[data-pm-cart-membership]')?.textContent || 'null'); }
    catch (_) { this.membership = null; }
    this.club = this.querySelector('[data-pm-cart-club]');
    this.clubTerms = this.querySelector('[data-pm-cart-club-terms]');
    /* Enrollment readiness starts unverified on every page view, so the drawer
       opens in the closed state the server rendered and can only leave it after
       a forced network read of the published offer. */
    this.freshVerified = false;
    this.freshPending = false;
    this.freshChecked = false;
    if (['_ab', '_fd', '_sc', 'preview_theme_id'].some((key) => new URLSearchParams(location.search).has(key))) {
      this.dataset.pmPreview = 'true';
    }
    this.abortController = new AbortController();
    this.lastActiveElement = null;

    this.querySelectorAll('[data-pm-cart-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(), { signal: this.abortController.signal });
    });
    this.keepShopping?.addEventListener('click', () => this.close(), { signal: this.abortController.signal });
    this.querySelector('[data-pm-cart-clear]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.clearCart(event.currentTarget);
    }, { signal: this.abortController.signal });
    this.querySelector('[data-pm-cart-club-add]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.addMembership(event.currentTarget);
    }, { signal: this.abortController.signal });
    // The upsell depends on verified entitlement, so it re-evaluates whenever the
    // authenticated session or membership state changes. One listener pair, scoped
    // to this element's abort signal.
    ['pk:member-state', 'pk:auth-state', 'pk:offer-state'].forEach((name) => {
      addEventListener(name, () => this.renderClubUpsell(this.lastCart), { signal: this.abortController.signal });
    });
    /* Test-account member prices follow its verified benefits (demoView). */
    ['pk:member-state', 'pk:auth-state'].forEach((name) => {
      addEventListener(name, () => {
        if (this.lastCart && this.demoPriceActive() !== !!this.demoPriced) this.render(this.lastCart);
      }, { signal: this.abortController.signal });
    });
    /* Identity decides whether the membership line is listed at all, so a change
       repaints the whole cart from the last read (no network). */
    addEventListener('pk:identity', () => { if (this.lastCart) this.render(this.lastCart); }, { signal: this.abortController.signal });
    /* Bank Pup Club choice: until the shopper picks Join Pup Club or No savings,
       the checkout control only points them to the choice. The button is never
       disabled in HTML, so a script failure cannot block a purchase. */
    this.addEventListener('click', (event) => {
      const checkout = event.target.closest(PmCartDrawer.CHECKOUT);
      if (!checkout || this.clubChoiceState !== 'ask') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const chooser = this.lines.querySelector('[data-pm-club-chooser]');
      if (!chooser) return;
      chooser.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      chooser.classList.remove('is-nudge');
      void chooser.offsetWidth;
      chooser.classList.add('is-nudge');
    }, { capture: true, signal: this.abortController.signal });
    /* Disabling the control is the visible refusal; this is the one that holds if
       the form is submitted any other way. A cart carrying a membership line the
       provider cannot yet capture must not reach Shopify checkout.
       An ordinary product cart submits straight through, untouched. A cart that
       holds a membership line does not: reaching checkout with it is the
       enrollment unlock, so the published offer is re-read from the server at
       that moment and the submit is replayed only when the whole contract comes
       back ready. A verdict this page reached earlier is not enough. */
    this.addEventListener('submit', (event) => {
      const submitter = event.submitter || this.querySelector(PmCartDrawer.CHECKOUT);
      if (!submitter || !submitter.matches(PmCartDrawer.CHECKOUT)) return;
      if (!this.staleMembershipLine(this.lastCart)) return;
      const form = event.target;
      if (this.membershipCheckoutCleared === form) {
        this.membershipCheckoutCleared = null;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.status.textContent = 'Checking the current Pup Club terms…';
      this.freshOfferReadiness().then((readiness) => {
        this.renderClubUpsell(this.lastCart);
        if (!readiness.ready) {
          this.status.textContent = this.enrollmentClosedMessage()
            + ' Remove Pup Club Membership from your cart, or use Clear cart, to check out.';
          return;
        }
        this.status.textContent = '';
        this.membershipCheckoutCleared = form;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
        else form.submit();
      });
    }, { capture: true, signal: this.abortController.signal });
    this.lines.addEventListener('click', (event) => {
      const filterButton = event.target.closest('[data-pm-cart-filter-change]');
      if (filterButton) {
        this.changeFilter(filterButton.dataset.pmCartFilterKey, filterButton.dataset.pmCartFilterChange, filterButton);
        return;
      }
      const lineButton = event.target.closest('[data-pm-cart-line-change]');
      if (lineButton) {
        this.changeLine(lineButton.dataset.pmCartLineKey, lineButton.dataset.pmCartLineChange, lineButton);
        return;
      }
      const button = event.target.closest('[data-pm-remove-group]');
      if (!button) return;
      this.removeGroup(button.dataset.pmRemoveGroup, button);
    }, { signal: this.abortController.signal });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-pm-cart-trigger]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    }, { signal: this.abortController.signal });
    document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.abortController.signal });
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.abortController?.abort();
    clearTimeout(this.closeTimer);
    document.documentElement.classList.remove('pm-cart-drawer-open');
    delete this.dataset.ready;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency || 'USD' }).format(Number(cents || 0) / 100);
  }

  errorText(error, fallback) {
    if (this.hasAttribute('data-pk-line-controls') && error instanceof TypeError) return 'We couldn’t reach your cart. Please try again.';
    return error.message || fallback;
  }

  async fetchCart() {
    const response = await fetch(this.cartJsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load the cart.');
    const cart = await response.json();
    /* Test account: learn which lines carry member prices before the first paint. */
    if (this.demoPriceActive()) await Promise.all((cart.items || []).map((item) => this.clubEligibility(item.handle)));
    return cart;
  }

  cartEditBody(body) {
    // A Bank edit must not leave an earlier Meta visit selecting the empty cart.
    if (this.hasAttribute('data-pk-line-controls') && !this.hasAttribute('data-pk-meta-drawer')) {
      return { ...body, attributes: { ...(body.attributes || {}), _s: '' } };
    }
    /* 2026-10-09: a Meta drawer edit writes the same hidden _s marker the Meta cart
       page forms write, so an emptied Meta cart reopened at plain /cart stays Meta
       (snippets/pk-meta-scope.liquid). */
    if (this.hasAttribute('data-pk-meta-drawer')) {
      return { ...body, attributes: { ...(body.attributes || {}), _s: '1' } };
    }
    return body;
  }

  /* Meta drawer: the Pup Club trial the Meta checkout handoff adds for the purifier
     offer (_pk_enrollment_source purifier-offer) leaves with the last Meta purifier,
     so no orphan trial follows the shopper into the Bank cart. */
  isOfferTrial(item) {
    return !!item && Number(item.variant_id || item.id) === 62739447119953 &&
      item.properties?._pk_enrollment_source === 'purifier-offer';
  }

  async removeGroup(groupToken, button) {
    if (!groupToken || button.disabled || this.updating) return false;
    this.updating = true;
    button.disabled = true;
    this.status.textContent = 'Removing this offer…';
    try {
      const currentCart = await this.fetchCart();
      const groupLines = (currentCart.items || []).filter((item) => item.properties?.['_PM parent offer'] === groupToken);
      if (!groupLines.length || groupLines.some((item) => !item.key)) {
        throw new Error('This offer could not be identified safely. Your cart was not changed.');
      }
      const updates = Object.fromEntries(groupLines.map((item) => [item.key, 0]));
      if (this.hasAttribute('data-pk-meta-drawer') && !(currentCart.items || []).some((item) =>
        Number(item.product_id) === 16333352992849 && item.properties?.['_PM parent offer'] !== groupToken)) {
        (currentCart.items || []).forEach((item) => { if (this.isOfferTrial(item) && item.key) updates[item.key] = 0; });
      }
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(this.cartEditBody({ updates }))
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'Unable to remove this offer.');
      }
      const cart = await response.json();
      if ((cart.items || []).some((item) => item.properties?.['_PM parent offer'] === groupToken)) {
        throw new Error('The complete offer could not be removed. Please try again.');
      }
      this.render(cart);
      this.panel?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('pm:cart-updated', {
        bubbles: true,
        detail: { action: 'remove-offer' }
      }));
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to remove this offer.');
      button.disabled = false;
      return false;
    } finally {
      this.updating = false;
    }
  }

  async clearCart(control) {
    if (this.updating || control?.getAttribute('aria-disabled') === 'true') return false;
    this.updating = true;
    control?.setAttribute('aria-disabled', 'true');
    this.status.textContent = 'Clearing your cart…';
    try {
      // Bank and (2026-10-09) Meta clear with one zero-all update, so the surface
      // marker (cartEditBody) is written in the same request.
      const edit = this.hasAttribute('data-pk-line-controls') || this.hasAttribute('data-pk-meta-drawer');
      const current = edit ? await this.fetchCart() : null;
      const body = edit ? this.cartEditBody({ updates: Object.fromEntries(current.items.map((item) => [item.key, 0])) }) : null;
      const response = await fetch(edit ? this.cartUpdateUrl : this.cartClearUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(this.cartEditBody(body)) } : {})
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to clear your cart.');
      const cart = Array.isArray(payload.items) ? payload : await this.fetchCart();
      this.render(cart);
      this.status.textContent = 'Your cart is empty.';
      this.panel?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'clear-cart' } }));
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to clear your cart.');
      control?.removeAttribute('aria-disabled');
      return false;
    } finally {
      this.updating = false;
    }
  }

  /* 2026-10-09 release-5: the "refreshed" notice is for a cart the server actually
     changed (a clamped or refused quantity). The line is matched by key, and when
     Shopify re-keys or splits it, by its variant and offer group, so a normal
     +/- reads as a success. */
  sameLine(a, b) {
    return Number(a?.variant_id || a?.id) === Number(b?.variant_id || b?.id)
      && String(a?.properties?.['_PM parent offer'] || '') === String(b?.properties?.['_PM parent offer'] || '')
      && String(a?.properties?.['_PM line role'] || '') === String(b?.properties?.['_PM line role'] || '');
  }

  serverChanged(before, after, item, quantity) {
    const lines = Array.isArray(after?.items) ? after.items : [];
    const byKey = lines.find((line) => line.key === item.key);
    if (byKey && Number(byKey.quantity) === quantity) return false;
    const total = (cart) => (cart?.items || []).filter((line) => this.sameLine(line, item))
      .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    return total(after) !== total(before) - (Number(item.quantity) || 0) + quantity;
  }

  isEditableFilter(item) {
    return Number(item.variant_id || item.id) === Number(this.catalog?.filterId) &&
      Number(this.catalog?.filterId) > 0 && Number(item.original_price) > 0 && !item.selling_plan_allocation;
  }

  async changeFilter(key, change, button) {
    if (!key || button.disabled || this.updating || !['-1', '1', 'remove'].includes(change)) return false;
    this.updating = true;
    button.disabled = true;
    this.status.textContent = 'Updating replacement filters…';
    try {
      const before = await this.fetchCart();
      const item = before.items.find((line) => line.key === key);
      if (!item || !this.isEditableFilter(item)) throw new Error('These filters could not be identified safely. Please reopen your cart.');
      const quantity = change === 'remove' ? 0 : item.quantity + Number(change);
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99) throw new Error('Please choose between 0 and 99 replacement filters.');
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(this.cartEditBody({ updates: { [key]: quantity } }))
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to update replacement filters.');
      this.render(payload);
      if (this.serverChanged(before, payload, item, quantity)) throw new Error('Your cart has been refreshed. Please check the available filter quantity.');
      this.status.textContent = quantity ? 'Replacement filters updated.' : 'Replacement filters removed.';
      this.panel?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'update-filter' } }));
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to update replacement filters.');
      button.disabled = false;
      return false;
    } finally {
      this.updating = false;
    }
  }

  // Bank-site shop view only (data-pk-line-controls): a standalone catalogue line can
  // change quantity or be removed. Offer groups, gifts and the filter keep their own
  // controls above, so the acquisition drawer is unchanged.
  hasLineControls(item) {
    return this.hasAttribute('data-pk-line-controls') && !item.properties?.['_PM parent offer'] &&
      this.lineRole(item) !== 'Gift' && !this.isEditableFilter(item) && !item.selling_plan_allocation;
  }

  async changeLine(key, change, button) {
    if (!key || button.disabled || this.updating || !['-1', '1', 'remove'].includes(change)) return false;
    this.updating = true;
    button.disabled = true;
    this.status.textContent = 'Updating your cart…';
    try {
      const before = await this.fetchCart();
      const item = before.items.find((line) => line.key === key);
      if (!item || !this.hasLineControls(item)) throw new Error('This item could not be identified safely. Please reopen your cart.');
      const quantity = change === 'remove' ? 0 : item.quantity + Number(change);
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99) throw new Error('Please choose a quantity between 0 and 99.');
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(this.cartEditBody({ updates: { [key]: quantity } }))
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to update your cart.');
      this.render(payload);
      if (this.serverChanged(before, payload, item, quantity)) throw new Error('Your cart has been refreshed. Please check the available quantity.');
      this.status.textContent = quantity ? 'Cart updated.' : (payload.items?.length ? 'Item removed.' : 'Your cart is empty.');
      this.panel?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'update-line' } }));
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to update your cart.');
      button.disabled = false;
      return false;
    } finally {
      this.updating = false;
    }
  }

  async open(returnFocusElement = document.activeElement) {
    clearTimeout(this.closeTimer);
    this.lastActiveElement = returnFocusElement;
    this.hidden = false;
    this.status.textContent = 'Loading your cart…';
    this.lines.replaceChildren();
    this.footer.hidden = true;
    // Reading a layout value flushes the just-applied pre-open state (hidden -> shown,
    // panel still at translateX(100%)) so adding is-open on the next line still animates
    // the slide-in. requestAnimationFrame alone was not safe: its callback only runs when
    // the tab actually paints, so on a backgrounded or occluded desktop window the class
    // never landed and the drawer sat 'open' -- page scroll locked, panel parked
    // off-screen to the right, no visible cart.
    this.panel?.getBoundingClientRect();
    this.classList.add('is-open');
    document.documentElement.classList.add('pm-cart-drawer-open');
    this.panel?.focus({ preventScroll: true });
    try {
      const cart = await this.fetchCart();
      this.render(cart);
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to load the cart.');
      return false;
    }
  }

  /* 2026-10-09 release-5: after an add, the line that was just added or increased is
     scrolled into view inside the drawer (the panel opens at the top, where offer
     groups can push a new standalone line out of sight). Without an earlier cart on
     this page, Shopify lists the newest line first. Not on the Meta drawer. */
  async openFromCartUpdate(returnFocusElement) {
    const prior = Array.isArray(this.lastCart?.items)
      ? new Map(this.lastCart.items.map((line) => [line.key, Number(line.quantity) || 0])) : null;
    const opened = await this.open(returnFocusElement);
    if (opened && !this.hasAttribute('data-pk-meta-drawer')) this.revealChanged(prior);
    return opened;
  }

  revealChanged(prior) {
    const rows = [...(this.lines?.querySelectorAll('[data-pm-cart-line-item]') || [])];
    const changed = (this.lastCart?.items || []).filter((line) => !prior || !prior.has(line.key) || (Number(line.quantity) || 0) > prior.get(line.key));
    for (const line of changed) {
      const row = rows.find((node) => node.dataset.pmCartLineItem === String(line.key));
      if (!row || typeof row.scrollIntoView !== 'function') continue;
      const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
      return row;
    }
    return null;
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    document.documentElement.classList.remove('pm-cart-drawer-open');
    const finish = () => {
      this.hidden = true;
      this.lastActiveElement?.focus?.({ preventScroll: true });
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else this.closeTimer = setTimeout(finish, 220);
  }

  handleKeydown(event) {
    if (this.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;
    const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length);
    if (!focusable.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  groupedItems(items) {
    const groups = new Map();
    const standalone = [];
    items.forEach((item) => {
      const token = item.properties?.['_PM parent offer'];
      if (!token) {
        standalone.push(item);
        return;
      }
      if (!groups.has(token)) groups.set(token, []);
      groups.get(token).push(item);
    });
    return { groups: [...groups.entries()], standalone };
  }

  makeText(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = text;
    return element;
  }

  lineRole(item) {
    return item.properties?.['_PM line role'] || 'Item';
  }

  visibleLineRole(role) {
    return role === 'Parent' ? '★ Best Seller' : role;
  }

  displayVariantTitle(item) {
    // Approved tier display labels come from snippets/pm-offer-purchase-stack.liquid,
    // matched to the live variant by price and shipped quantity. Purifier lines only;
    // an unrecognised variant keeps its own catalog title, never a tier label.
    if (!String(item?.handle || '').startsWith('pet-air-purifier')) return item?.variant_title;
    // Pack size is option 1; a colour combo title reads "3 Pack / 2 Black + 1 White".
    const pack = item.variant_options?.[0] || item.variant_title;
    return { '1 Unit': '1 Unit', '1 Pack': '1 Unit',
             '2 + 1 FREE': '2 + 1 FREE', '3 Pack': '2 + 1 FREE',
             '3 + 2 FREE': '3 + 2 FREE', '5 Pack': '3 + 2 FREE' }[pack] || item.variant_title;
  }

  purifierUnits(item) {
    const variant = this.catalog?.purifierVariants?.find((entry) => Number(entry.id) === Number(item.variant_id || item.id));
    // 'N Pack' titles are live per evidence/REMOTE-PRODUCTS.json; legacy titles are kept deliberately.
    return { '1 Unit': 1, '2 + 1 FREE': 3, '3 + 2 FREE': 5,
             '1 Pack': 1, '3 Pack': 3, '5 Pack': 5 }[variant?.option1 || variant?.title] || 0;
  }

  bankDrawer() {
    return this.hasAttribute('data-pk-line-controls') && !this.hasAttribute('data-pk-meta-drawer');
  }

  giftValue(item) {
    return Math.max(0, Number(item?.original_line_price || 0) - Number(item?.final_line_price || 0));
  }

  cartSavings(cart) {
    // Real discounts are already included once in Shopify's original-to-final
    // difference. Bundle savings compare shipped purifiers with the current
    // one-unit SKU, never visual compare-at strings or gift value estimates.
    const single = this.catalog?.purifierVariants?.find((variant) => ['1 Unit', '1 Pack'].includes(variant.option1 || variant.title));
    const bundleSavings = (cart.items || []).reduce((sum, item) => {
      const units = this.purifierUnits(item);
      if (units < 2 || item.selling_plan_allocation || !Number.isFinite(Number(single?.price)) || !Number.isFinite(item.original_price)) return sum;
      return sum + Math.max(0, Number(single.price) * units - item.original_price) * item.quantity;
    }, 0);
    return bundleSavings + Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
  }

  hasFreeShipping(cart) {
    // A static offer badge is not evidence of a shipping rate. Only a real
    // full shipping discount reported by Shopify can unlock this cart claim.
    const verified = (cart.cart_level_discount_applications || []).some((discount) =>
      discount.target_type === 'shipping_line' && discount.value_type === 'percentage' && Number(discount.value) === 100);
    if (!verified) return false;
    const items = cart.items || [];
    const giftIds = (this.catalog?.giftIds || []).map(Number).filter((id) => id > 0);
    return items.some((item) => this.purifierUnits(item) > 0) && items.every((item) =>
      this.purifierUnits(item) > 0 || this.isEditableFilter(item) ||
      (giftIds.includes(Number(item.variant_id || item.id)) && item.final_line_price === 0));
  }

  parentColors(item) {
    const properties = item.properties || {};
    const indexedColors = new Map();
    Object.entries(properties).forEach(([key, value]) => {
      const publicMatch = key.match(/^Color #(\d+)$/);
      if (publicMatch && value) indexedColors.set(Number(publicMatch[1]), String(value));
    });
    Object.entries(properties).forEach(([key, value]) => {
      const privateMatch = key.match(/^_PM color (\d+)$/);
      const index = Number(privateMatch?.[1]);
      if (privateMatch && value && !indexedColors.has(index)) indexedColors.set(index, String(value));
    });
    if (indexedColors.size) {
      return [...indexedColors.entries()]
        .sort(([indexA], [indexB]) => indexA - indexB)
        .map(([index, value]) => ({ index, value }));
    }

    return String(properties['_PM selected colors'] || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value, index) => ({ index: index + 1, value }));
  }

  renderLine(item) {
    const line = document.createElement('div');
    const role = this.lineRole(item);
    line.className = 'pm-cart-drawer__line';
    line.dataset.pmCartLineRole = role;
    if (!this.hasAttribute('data-pk-meta-drawer')) line.dataset.pmCartLineItem = String(item.key || '');

    const imageSource = String(item.handle || '').startsWith('pet-air-purifier') && this.dataset.purifierImage
      ? this.dataset.purifierImage : item.image;
    if (imageSource) {
      const image = document.createElement('img');
      image.src = imageSource;
      image.width = 72;
      image.height = 72;
      image.loading = 'lazy';
      image.alt = '';
      line.append(image);
    }

    const body = document.createElement('div');
    body.className = 'pm-cart-drawer__line-body';
    const clubAmount = this.bankClubEnabled() ? this.clubAmount(item) : 0;
    const roleBadge = this.makeText('span', 'pm-cart-drawer__role', clubAmount > 0 ? '50% off with Pup Club' : this.visibleLineRole(role));
    if (role === 'Parent' && clubAmount <= 0) {
      roleBadge.replaceChildren(this.makeText('span', 'pm-cart-drawer__star', '★'), document.createTextNode(' Best Seller'));
    }
    body.append(roleBadge);
    body.append(this.makeText('strong', '', item.product_title || item.title || 'Product'));
    if (item.variant_title && item.variant_title !== 'Default Title') {
      body.append(this.makeText('small', '', this.displayVariantTitle(item)));
    }
    if (role === 'Parent') {
      const colors = this.parentColors(item);
      if (colors.length) {
        const colorSummary = colors.map((color) => `#${color.index} ${color.value}`).join(', ');
        const colorLine = this.makeText('small', 'pm-cart-drawer__colors', `Purifier colors: ${colorSummary}`);
        colorLine.dataset.pmCartColors = colors.map((color) => color.value).join('|');
        body.append(colorLine);
        line.dataset.pmCartColorSignature = colors.map((color) => color.value).join('|');
      }
    }
    const units = this.purifierUnits(item);
    if (units) {
      const purifiers = units * item.quantity;
      body.append(this.makeText('small', 'pm-cart-drawer__bundle-count', `${item.quantity} bundle${item.quantity === 1 ? '' : 's'} · ${purifiers} purifier${purifiers === 1 ? '' : 's'}`));
    } else if (role === 'Filter' || this.isEditableFilter(item)) {
      body.append(this.makeText('small', '', `Qty ${item.quantity} × ${this.formatMoney(item.final_price)} each`));
    } else if (role !== 'Gift') {
      body.append(this.makeText('small', '', `Qty ${item.quantity}`));
    }
    const refill = this.refillTerms(item);
    if (refill) body.append(refill);
    if (this.isEditableFilter(item)) {
      const controls = document.createElement('div');
      controls.className = 'pm-cart-drawer__filter-controls';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Edit replacement filters');
      [['−', '-1', 'Decrease replacement filter quantity'], ['+', '1', 'Increase replacement filter quantity'], ['Remove', 'remove', 'Remove replacement filters']].forEach(([text, change, label]) => {
        const button = this.makeText('button', '', text);
        button.type = 'button';
        button.dataset.pmCartFilterKey = item.key;
        button.dataset.pmCartFilterChange = change;
        button.setAttribute('aria-label', label);
        if (change === '1' && item.quantity >= 99) button.disabled = true;
        controls.append(button);
      });
      body.append(controls);
    }
    if (this.hasLineControls(item)) {
      const controls = document.createElement('div');
      controls.className = 'pm-cart-drawer__filter-controls';
      controls.setAttribute('role', 'group');
      const name = item.product_title || 'item';
      controls.setAttribute('aria-label', `Edit ${name}`);
      [['−', '-1', `Decrease quantity of ${name}`], ['+', '1', `Increase quantity of ${name}`], ['Remove', 'remove', `Remove ${name}`]].forEach(([text, change, label]) => {
        const button = this.makeText('button', '', text);
        button.type = 'button';
        button.dataset.pmCartLineKey = item.key;
        button.dataset.pmCartLineChange = change;
        button.setAttribute('aria-label', label);
        if (change === '1' && item.quantity >= 99) button.disabled = true;
        controls.append(button);
      });
      body.append(controls);
    }
    line.append(body);
    const price = this.makeText('strong', 'pm-cart-drawer__line-price', role === 'Gift' ? 'FREE' : this.formatMoney(item.final_line_price));
    if (clubAmount > 0 && role !== 'Gift') {
      price.classList.add('is-club');
      price.append(this.makeText('span', 'pm-cart-drawer__line-was', `Guest ${this.formatMoney(item.original_line_price)}`));
    }
    /* Bank drawer (2026-10-08): each free gift shows the value it carries, so
       "You save" can be added up from what the drawer shows. */
    const giftValue = role === 'Gift' ? this.giftValue(item) : 0;
    if (giftValue > 0 && this.bankDrawer()) {
      const was = this.makeText('span', 'pm-cart-drawer__line-was', this.formatMoney(giftValue));
      price.setAttribute('aria-label', `Free, normally ${this.formatMoney(giftValue)}`);
      price.append(was);
    }
    line.append(price);
    return line;
  }

  /* Paid filter refill terms (2026-10-08), Bank drawer only: the exact recurring
     variant 62738632179793 / PK-MTA-FLT-SUB, never the same-name one-time filters,
     and never in a cart holding the Meta purifier (product 16333352992849). */
  refillTerms(item) {
    if (!this.hasAttribute('data-pk-line-controls') || this.hasAttribute('data-pk-meta-drawer')) return null;
    if (Number(item.variant_id || item.id) !== 62738632179793) return null;
    if ((this.lastCart?.items || []).some((line) => Number(line.product_id) === 16333352992849)) return null;
    const quantity = Number(item.quantity) || 1;
    const unit = Number(item.final_price) || 0;
    const note = document.createElement('p');
    note.className = 'pkb-vc__gist';
    note.dataset.pkRefillTerms = '';
    note.append(this.makeText('b', '', 'Filter refill:'),
      document.createTextNode(` ${quantity > 1 ? `${this.formatMoney(unit)} × ${quantity} = ` : ''}${this.formatMoney(unit * quantity)} today and every 30 days, plus applicable tax and shipping, until you cancel. `));
    const cancel = this.makeText('a', 'pkb-vc__more', 'Cancel anytime here');
    cancel.href = '/pages/cancel-membership';
    cancel.dataset.pkRoute = 'cancel';
    note.append(cancel, document.createTextNode('.'));
    return note;
  }

  renderGroup(token, items) {
    const group = document.createElement('section');
    group.className = 'pm-cart-drawer__group';
    group.dataset.pmCartGroup = token;
    const parent = items.find((item) => this.lineRole(item) === 'Parent');
    const groupHeader = document.createElement('div');
    groupHeader.className = 'pm-cart-drawer__group-header';
    const heading = this.makeText('h3', '', parent ? `${parent.product_title}: ${this.displayVariantTitle(parent)}` : 'Puppy Kind offer');
    const remove = this.makeText('button', 'pm-cart-drawer__remove', 'Remove offer');
    remove.type = 'button';
    remove.dataset.pmRemoveGroup = token;
    remove.setAttribute('aria-label', `Remove ${parent?.product_title || 'Puppy Kind'} offer and its included items`);
    groupHeader.append(heading, remove);
    group.append(groupHeader);
    const ordered = [...items].sort((a, b) => {
      const order = { Parent: 0, Gift: 1, Filter: 2 };
      const roleDelta = (order[this.lineRole(a)] ?? 3) - (order[this.lineRole(b)] ?? 3);
      if (roleDelta) return roleDelta;
      if (this.lineRole(a) === 'Gift') {
        return Number(a.properties?.['_PM gift slot'] || 0) - Number(b.properties?.['_PM gift slot'] || 0);
      }
      return 0;
    });
    ordered.forEach((item) => group.append(this.renderLine(item)));
    return group;
  }

  /* The enrollment contract is owned by pk-vip-pup-club.js. The drawer asks it and
     never re-implements it, so the four provider clauses, the terms version and the
     server offer id cannot drift between the drawer, the cart page and the portal.
     No club script (Meta, or a Bank page where the bundle failed) means not ready. */
  offerReadiness() {
    const club = window.PKPupClub;
    if (!club || typeof club.offerReadiness !== 'function') {
      return { ready: false, reasons: ['club_unavailable'] };
    }
    try { return club.offerReadiness(); } catch (_) { return { ready: false, reasons: ['club_error'] }; }
  }

  enrollmentClosedMessage() {
    const club = window.PKPupClub;
    const fallback = 'Pup Club enrollment is not open yet. Nothing has been added, started or charged.';
    if (!club || typeof club.enrollmentClosedMessage !== 'function') return fallback;
    try { return club.enrollmentClosedMessage() || fallback; } catch (_) { return fallback; }
  }

  /* The forced-network form of that contract, also owned by pk-vip-pup-club.js.
     Calling club.refreshOffer() was not enough on its own: without the force
     flag it returns the offer this page already holds, so the "fresh" check was
     answering from cache. There is no local re-fetch fallback — a client that
     cannot offer the forced gate is treated as unavailable, which is closed. */
  freshOfferReadiness() {
    const club = window.PKPupClub;
    if (!club || typeof club.freshOfferReadiness !== 'function') {
      this.freshVerified = false;
      return Promise.resolve({ ready: false, reasons: ['club_unavailable'], fresh: false });
    }
    return Promise.resolve(club.freshOfferReadiness())
      .then((readiness) => {
        this.freshVerified = !!(readiness && readiness.ready === true);
        return readiness;
      })
      .catch(() => {
        this.freshVerified = false;
        return { ready: false, reasons: ['offer_unavailable'], fresh: false };
      });
  }

  /* Only a completed successful forced refresh may lift a closed state in this
     drawer. It starts false on every page view, so no club script, a failed
     fetch, a closed contract or a disabled provider all leave the membership
     upsell hidden and the membership checkout block in place. One automatic
     verification per view; every add still forces its own immediately before
     it acts. */
  verifyFreshReadiness() {
    if (this.freshPending || this.freshChecked) return;
    this.freshPending = true;
    this.freshOfferReadiness().finally(() => {
      this.freshPending = false;
      this.freshChecked = true;
      this.renderClubUpsell(this.lastCart);
    });
  }

  freshReady() {
    return this.freshVerified === true && this.offerReadiness().ready === true;
  }

  /* A membership line left in the cart from an earlier session is not a purchase
     the storefront may complete while enrollment is closed: checkout would take a
     subscription the provider cannot capture or renew. The line is reported, the
     checkout control is disabled, and the shopper is given the remove and clear
     controls that are already in the footer. */
  staleMembershipLine(cart) {
    return false;
  }

  enforceMembershipCheckoutGuard(cart) {
    const checkout = this.querySelector(PmCartDrawer.CHECKOUT);
    if (!checkout) return false;
    checkout.disabled = false;
    if (!checkout.classList.contains('is-locked')) checkout.removeAttribute('aria-disabled');
    delete checkout.dataset.pkBlockedReason;
    return false;
  }

  // One versioned source for the offer wording: the server offer record when it is
  // present, otherwise the theme's plan record. Never authored in this file.
  // An add path passes the exact response that authorized it, so the wording on
  // the line it writes cannot come from a different read than the id it carries.
  membershipTerms(snapshot) {
    const cents = Number(this.membership?.priceCents);
    return cents > 0 ? `21-day free trial, then ${this.formatMoney(cents)} every 28 days` : 'See checkout for current pricing';
  }

  // Shown only for a Bank cart that does not already hold the membership, when the
  // verified session reports no active entitlement, AND when the server's published
  // offer satisfies the whole enrollment contract. An error, an unknown state or a
  // closed contract leaves the upsell hidden; it never appears twice.
  renderClubUpsell(cart) {
    const target = cart === undefined ? this.lastCart : cart;
    const blocked = this.enforceMembershipCheckoutGuard(target);
    if (blocked && this.status && !this.status.textContent) {
      this.status.textContent = this.enrollmentClosedMessage()
        + ' Remove Pup Club Membership from your cart, or use Clear cart, to check out.';
    }
    if (this.bankClubEnabled()) {
      if (this.club) this.club.hidden = true;
      this.renderClubChooser(target);
      return;
    }
    if (!this.club) return;
    const variantId = Number(this.membership?.variantId);
    const terms = this.membershipTerms();
    const auth = window.PK_PUP_CLUB_AUTH || {};
    const lines = target && Array.isArray(target.items) ? target.items : [];
    const present = lines.some((item) => this.isClubLine(item));
    const eligible = !!this.hasAttribute('data-pk-line-controls') && variantId > 0 && !!terms &&
      lines.length > 0 && !present && auth.activeMember !== true;
    if (eligible && this.clubTerms) this.clubTerms.textContent = terms;
    this.club.hidden = !eligible;
  }

  async addMembership(control) {
    const variantId = Number(this.membership?.variantId);
    if (this.updating || !(variantId > 0) || control?.disabled) return false;
    /* Only a signed-out shopper may add the trial. A member, the test account and
       a session still being verified are refused before any cart write. */
    const who = this.identity();
    if (who !== 'guest') {
      this.status.textContent = this.enrollRefusal(who);
      return false;
    }
    this.updating = true;
    if (control) control.disabled = true;
    this.status.textContent = 'Adding Pup Club to your cart\u2026';
    const line = { id: variantId, quantity: 1 };
    const terms = this.membershipTerms();
    line.properties = { 'Pup Club plan': terms };
    if (Number.isInteger(this.membership?.sellingPlanId) && this.membership.sellingPlanId > 0) {
      line.selling_plan = this.membership.sellingPlanId;
    }
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [line] })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add Pup Club Membership.');
      await this.applyJoinCode(true, { _pk_club_choice: '' });
      const cart = await this.fetchCart();
      this.render(cart);
      this.status.textContent = 'Pup Club added to your cart. Complete checkout to start your membership.';
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'add-membership' } }));
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to add Pup Club Membership. Your cart was not changed.');
      return false;
    } finally {
      this.updating = false;
      if (control) control.disabled = false;
    }
  }

  /* Bank only: the joining-order code (50% off the member catalog on the order
     that starts Pup Club) follows the membership line. The code comes from
     snippets/pk-club-join-code.liquid through the membership descriptor, which
     the Meta drawer never renders. */
  joinCodeList(cart, withCode) {
    const code = String(this.membership?.joinCode || '').trim();
    const others = (cart?.discount_codes || []).map((entry) => entry.code)
      .filter((value) => value && value.toUpperCase() !== code.toUpperCase());
    return withCode && code ? others.concat(code) : others;
  }

  async applyJoinCode(withCode, attributes) {
    if (!String(this.membership?.joinCode || '').trim()) return null;
    try {
      const current = await this.fetchCart();
      const body = { discount: this.joinCodeList(current, withCode).join(',') };
      if (attributes) body.attributes = attributes;
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(this.cartEditBody(body))
      });
      return response.ok ? response.json() : null;
    } catch (_) {
      return null;
    }
  }

  /* removeOnly (a signed-in account without active benefits): the joining code is
     only ever taken off. Phoenix gives the 50% only to an order that starts Pup
     Club, so a code without a Pup Club line would show prices checkout does not
     charge. Checkout is held while the write runs (A2 M3).
     memberPrice (2026-10-09 release-5): a member whose entitlement reply said
     memberPriceEligible (identity 'member') keeps the joining code, and gets it
     when the cart has none, so Phoenix applies the 50% at checkout. */
  syncJoinCode(cart, removeOnly, memberPrice) {
    const code = String(this.membership?.joinCode || '').trim().toUpperCase();
    if (!code || this.codeSyncing || !cart || !Array.isArray(cart.items)) return;
    const hasClub = cart.items.some((item) => this.isClubLine(item));
    const want = hasClub || memberPrice === true;
    const applied = (cart.discount_codes || []).some((entry) => String(entry.code || '').toUpperCase() === code);
    if (want === applied || (removeOnly && want)) return;
    const signature = `${cart.token}:${want}`;
    if (this.codeSyncSignature === signature) return;
    this.codeSyncSignature = signature;
    this.codeSyncing = true;
    const hold = 'Updating your cart\u2026 Please try again in a moment.';
    const held = !window.PK_CHECKOUT_HOLD;
    if (held) window.PK_CHECKOUT_HOLD = hold;
    if (!want) PmCartDrawer.forgetDiscountCookie(code);
    this.applyJoinCode(want).then((next) => {
      this.codeSyncing = false;
      if (held && window.PK_CHECKOUT_HOLD === hold) window.PK_CHECKOUT_HOLD = '';
      if (next && Array.isArray(next.items)) this.render(next);
    });
  }

  /* An empty Bank cart retires a Meta _s marker left from an earlier Meta visit, so
     plain /cart opens the Bank cart for this shopper. One write per page view. */
  retireMetaMarker(cart) {
    if (!this.bankDrawer() || this.markerRetired || cart?.attributes?._s !== '1') return;
    this.markerRetired = true;
    fetch(this.cartUpdateUrl, {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(this.cartEditBody({}))
    }).catch(() => {});
  }

  /* ---- Bank Pup Club choice (same options as the Bank cart page) ---- */
  bankClubEnabled() {
    return this.hasAttribute('data-pk-line-controls') && Number(this.membership?.variantId) > 0;
  }

  /* Bank only (the descriptor is never rendered on the Meta drawer): the current
     trial variant and an already-selected legacy Pup Club line are both the
     membership, so the join code survives until checkout migrates the line.
     Recognition only; nothing here adds a membership. */
  isClubLine(item) {
    const variantId = Number(this.membership?.variantId);
    if (!item || !(variantId > 0)) return false;
    return Number(item.variant_id || item.id) === variantId ||
      ['pup-club-free-trial', 'pup-club-membership'].includes(String(item.handle || ''));
  }

  /* Meta drawer only (data-pk-meta-drawer): the purifier offer enrolls Pup Club
     automatically, so its line stays in the Shopify cart for checkout but is not
     listed here. Display only; the cart, totals and checkout are untouched. */
  isMetaMembershipLine(item) {
    if (!item || !this.hasAttribute('data-pk-meta-drawer')) return false;
    return [62739447119953, 62649526026321].includes(Number(item.variant_id || item.id)) ||
      ['pup-club-free-trial', 'pup-club-membership'].includes(String(item.handle || ''));
  }

  clubAmount(item) {
    const code = String(this.membership?.joinCode || '').trim().toUpperCase();
    return (item?.line_level_discount_allocations || []).reduce((sum, allocation) => {
      const title = String(allocation?.discount_application?.title || '').toUpperCase();
      return (code && title === code) || title.includes('PUP CLUB ACTIVE') ? sum + Number(allocation.amount || 0) : sum;
    }, 0);
  }

  clubChoice(cart) {
    if (!this.bankClubEnabled() || !cart?.items?.length) return 'none';
    /* The choice is for signed-out shoppers only. Uncertain identity is not a guest. */
    if (this.identity() !== 'guest') return 'none';
    if (cart.items.some((item) => this.isClubLine(item))) return 'member';
    /* Full price is the Bank default until the shopper taps Join Pup Club
       (Zack, 2026-09-26). The selected No savings option always shows the
       not-saving line, the untouched default included (Zack, 2026-10-03). */
    return 'guest';
  }

  /* Same rule as the collection behind both Pup Club 50% discounts: tag
     pk-approved (the Bank purifier included since the 2026-09-27 repricing). cart.js has no tags, so each product is
     read once from /products/<handle>.js and remembered for the session. */
  eligibilityCache() {
    this.eligibility = this.eligibility || (() => {
      try { return JSON.parse(sessionStorage.getItem('pk-club-eligibility-v2') || '{}'); } catch (_) { return {}; }
    })();
    return this.eligibility;
  }

  clubEligibility(handle) {
    this.eligibilityCache();
    if (!handle || handle in this.eligibility) return Promise.resolve(this.eligibility[handle] === true);
    return fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',').map((tag) => tag.trim());
        const eligible = !!product && tags.includes('pk-approved');
        this.eligibility[handle] = eligible;
        try { sessionStorage.setItem('pk-club-eligibility-v2', JSON.stringify(this.eligibility)); } catch (_) { /* private mode */ }
        return eligible;
      })
      .catch(() => false);
  }

  async clubTotals(cart) {
    const items = (cart.items || []).filter((item) => !this.isClubLine(item));
    const clubSavings = items.reduce((sum, item) => sum + this.clubAmount(item), 0);
    const total = Number(cart.total_price || 0);
    if (cart.items.some((item) => this.isClubLine(item))) {
      return { join: total, guest: total + clubSavings, save: clubSavings };
    }
    const flags = await Promise.all(items.map((item) => (this.clubAmount(item) > 0 ? false : this.clubEligibility(item.handle))));
    const extra = items.reduce((sum, item, index) => {
      if (!flags[index]) return sum;
      const original = Number(item.original_line_price || 0);
      const memberLine = original - Math.floor(original / 2);
      return sum + Math.max(0, Number(item.final_line_price || 0) - memberLine);
    }, 0);
    return { join: total - extra, guest: total, save: extra };
  }

  /* Bank drawer, verified test account with active benefits only: the lines show
     the member price its product pages show (window.PKDemoPricing in
     snippets/pk-demo-action-lock.liquid). A display copy; the cart is untouched. */
  demoPriceActive() {
    if (!this.hasAttribute('data-pk-line-controls') || this.hasAttribute('data-pk-meta-drawer')) return false;
    try { return window.PKDemoPricing?.active() === true; } catch (_) { return false; }
  }

  demoView(cart) {
    this.demoPriced = this.demoPriceActive();
    if (!this.demoPriced) return cart;
    const cache = this.eligibilityCache();
    const unknown = new Set();
    const view = window.PKDemoPricing.project(cart, (item) => {
      if (this.isClubLine(item) || this.clubAmount(item) > 0) return false;
      const handle = String(item.handle || '');
      if (!handle) return false;
      if (!(handle in cache)) { unknown.add(handle); return false; }
      return cache[handle] === true;
    });
    this.eligibilityTried = this.eligibilityTried || new Set();
    const ask = [...unknown].filter((handle) => !this.eligibilityTried.has(handle));
    if (ask.length) {
      ask.forEach((handle) => this.eligibilityTried.add(handle));
      Promise.all(ask.map((handle) => this.clubEligibility(handle))).then(() => {
        if (this.lastCart === cart) this.render(cart);
      });
    }
    return view;
  }

  clubCheck() {
    return '<svg viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="9" fill="#F2EAF6"/><path d="M5.2 9.3l2.4 2.4 5.2-5.3" fill="none" stroke="#A12EB6" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  renderClubCard(cart, line) {
    const savings = (cart.items || []).reduce((sum, item) => sum + (this.isClubLine(item) ? 0 : this.clubAmount(item)), 0);
    const card = document.createElement('div');
    card.className = 'pkb-vc';
    const price = this.formatMoney(this.membership?.priceCents);
    card.innerHTML = `<span class="pkb-vc__tile"><img src="${this.dataset.clubCrown || ''}" width="24" height="24" alt=""></span>
      <div><span class="pkb-pill">Membership</span><p class="pkb-vc__head">Pup Club Membership</p>
      ${savings > 0 ? `<p class="pkb-vc__save">You're saving <b>${this.formatMoney(savings)}</b> with Pup Club!</p>` : ''}
      <p class="pkb-vc__gist">Free for 21 days, then ${price} every 28 days. Cancel anytime.</p></div>
      <button class="pkb-vc__x" type="button" aria-label="Remove Pup Club Membership">&#10005;</button>`;
    card.querySelector('.pkb-vc__x').addEventListener('click', (event) => this.chooseGuest(event.currentTarget));
    return card;
  }

  async renderClubChooser(cart) {
    const state = this.clubChoice(cart);
    this.clubChoiceState = state;
    this.paintClubGate(state);
    const existing = this.lines.querySelector('[data-pm-club-chooser]');
    if (state === 'none') { existing?.remove(); return; }
    const renderId = (this.chooserRenderId = (this.chooserRenderId || 0) + 1);
    const totals = await this.clubTotals(cart);
    if (renderId !== this.chooserRenderId || cart !== this.lastCart) return;
    const money = (cents) => this.formatMoney(cents);
    const price = money(this.membership?.priceCents);
    const filterCents = Number(this.membership?.filterPriceCents || 0);
    const filterMember = filterCents - Math.floor(filterCents / 2);
    const check = this.clubCheck();
    const chooser = document.createElement('div');
    chooser.className = `pkb-club${state === 'ask' ? ' pkb-club--ask' : ''}`;
    chooser.dataset.pmClubChooser = state;
    chooser.innerHTML = `${state === 'ask' ? '<p class="pkb-club__ask">Choose one to continue</p>' : ''}
      <div class="pkb-club__opts" role="radiogroup" aria-label="Pup Club">
        <button class="pkb-opt pkb-opt--club${state === 'member' ? ' is-on' : ''}" type="button" role="radio" aria-checked="${state === 'member'}" data-pm-club-pick="join">
          <span class="pkb-opt__lead"><img class="pkb-opt__crown" src="${this.dataset.clubCrown || ''}" width="13" height="13" alt="">Join Pup Club</span>
          <span class="pkb-opt__sub">Order total</span>
          <span class="pkb-opt__total">${money(totals.join)}</span>
          <span class="pkb-opt__pill">${totals.save > 0 ? `You save ${money(totals.save)}` : '50% off future orders'}</span>
          <span class="pkb-opt__note">21 days free, then ${price}</span>
        </button>
        <button class="pkb-opt pkb-opt--guest${state === 'guest' ? ' is-on' : ''}" type="button" role="radio" aria-checked="${state === 'guest'}" data-pm-club-pick="guest">
          <span class="pkb-opt__lead">No savings or perks</span>
          <span class="pkb-opt__sub">Order total</span>
          <span class="pkb-opt__total">${money(totals.guest)}</span>
          <span class="pkb-opt__pill pkb-opt__pill--none">Full price</span>
          <span class="pkb-opt__note">No member perks</span>
        </button>
      </div>
      ${state === 'guest' ? '<p class="pkb-club__none">You\u2019re not saving with Pup Club on this order.</p>' : `
      <ul class="pkb-club__perks">
        <li>${check}<span><b>50% off Puppy Kind products</b>${totals.save > 0 ? ', starting with this order' : ''}</span></li>
        ${filterCents > 0 ? `<li>${check}<span><b>Replacement filters at 50% off:</b> ${money(filterMember)} every time</span></li>` : ''}
        <li>${check}<span><b>Free for 21 days.</b> $0 for Pup Club today.</span></li>
        <li>${check}<span><b>Cancel online anytime.</b> No minimum.</span></li>
      </ul>
      <div class="pkb-club__terms">
        <p class="pkb-club__termhead">How Pup Club works</p>
        <div class="pkb-film" data-pk-film data-pk-film-hd="https://cdn.shopify.com/videos/c/vp/4af7c285e384445aa5aca641938f395b/4af7c285e384445aa5aca641938f395b.HD-1080p-3.3Mbps-96517088.mp4" data-pk-film-md="https://cdn.shopify.com/videos/c/vp/4af7c285e384445aa5aca641938f395b/4af7c285e384445aa5aca641938f395b.HD-720p-2.1Mbps-96517088.mp4" role="button" tabindex="0" aria-label="Play the Pup Club film"><img src="https://cdn.shopify.com/s/files/1/1070/2018/9777/files/pup-club-film-thumb.jpg?v=1790457095" alt="" width="1600" height="900" loading="lazy" decoding="async"><span class="pkb-film__cta"><span class="pkb-film__play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 4.8v14.4L19 12z"/></svg></span><span class="pkb-film__label">Watch<small>0:50</small></span></span></div>
      </div>`}`;
    chooser.querySelectorAll('[data-pm-club-pick]').forEach((button) => button.addEventListener('click', (event) => {
      const pick = event.currentTarget.dataset.pmClubPick;
      if (pick === 'join' && state !== 'member') this.addMembership(event.currentTarget);
      if (pick === 'guest' && state !== 'guest') this.chooseGuest(event.currentTarget);
    }));
    this.lines.querySelector('[data-pm-club-chooser]')?.remove();
    this.lines.append(chooser);
  }

  paintClubGate(state) {
    const checkout = this.querySelector(`[data-pm-cart-footer] :is(${PmCartDrawer.CHECKOUT})`);
    const hint = this.querySelector('[data-pm-cart-club-hint]');
    const off = state === 'ask';
    if (checkout) {
      checkout.classList.toggle('is-off', off);
      if (off) checkout.setAttribute('aria-disabled', 'true');
      else if (checkout.getAttribute('aria-disabled') === 'true' && !checkout.dataset.pkBlockedReason && !checkout.classList.contains('is-locked')) checkout.removeAttribute('aria-disabled');
    }
    if (hint) hint.hidden = !off;
  }

  async chooseGuest(control) {
    if (this.updating || control?.disabled) return false;
    this.updating = true;
    if (control) control.disabled = true;
    this.status.textContent = 'Updating your cart\u2026';
    try {
      const cart = await this.fetchCart();
      const body = { attributes: { _pk_club_choice: 'guest' }, discount: this.joinCodeList(cart, false).join(',') };
      const updates = {};
      (cart.items || []).forEach((item) => { if (this.isClubLine(item)) updates[item.key] = 0; });
      if (Object.keys(updates).length) body.updates = updates;
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(this.cartEditBody(body))
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to update your cart.');
      this.render(payload);
      this.status.textContent = '';
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'club-guest' } }));
      return true;
    } catch (error) {
      this.status.textContent = this.errorText(error, 'Unable to update your cart.');
      return false;
    } finally {
      this.updating = false;
      if (control) control.disabled = false;
    }
  }

  render(cart) {
    this.lastCart = cart;
    this.currency = cart.currency || 'USD';
    this.lines.replaceChildren();
    /* Bank: the membership line is listed only for a signed-out shopper who chose
       it. A member's stale line is removed below; the test account (shown as an
       existing member) and an unverified session never see an enrollment card. */
    const who = this.bankClubEnabled() ? this.identity() : 'guest';
    const view = this.demoView(cart);
    const enrollHidden = (item) => who !== 'guest' && this.isClubLine(item);
    const hidden = (view.items || []).filter((item) => this.isMetaMembershipLine(item) || enrollHidden(item));
    this.paintLockState(who);
    const items = hidden.length ? view.items.filter((item) => !hidden.includes(item)) : view.items;
    // Remove stale enrollment before the empty-view return, including for demo.
    if (who === 'member' || who === 'account' || who === 'demo') this.dropMemberEnrollment(cart, who);
    if (!items?.length) {
      if (!cart.item_count) this.retireMetaMarker(cart);
      this.status.textContent = 'Your cart is empty.';
      if (this.keepShopping) this.keepShopping.hidden = false;
      this.footer.hidden = true;
      this.renderClubUpsell(cart);
      this.updateCartCount(0);
      return;
    }

    if (this.keepShopping) this.keepShopping.hidden = true;
    const { groups, standalone } = this.groupedItems(items);
    const clubLine = this.bankClubEnabled() ? standalone.find((item) => this.isClubLine(item)) : null;
    if (clubLine) this.lines.append(this.renderClubCard(cart, clubLine));
    groups.forEach(([token, items]) => this.lines.append(this.renderGroup(token, items)));
    standalone.filter((item) => item !== clubLine).forEach((item) => this.lines.append(this.renderLine(item)));
    this.status.textContent = '';
    this.subtotal.textContent = this.formatMoney(view.total_price);
    /* The free-trial discount on the membership line is not a saving on this
       order's products, so the Bank drawer leaves it out of "You save". */
    const trial = (clubLine ? [clubLine] : hidden).reduce((sum, line) =>
      sum + Math.max(0, Number(line.original_line_price || 0) - Number(line.final_line_price || 0)), 0);
    const savings = Math.max(0, this.cartSavings(view) - trial);
    this.savings.textContent = this.formatMoney(savings);
    // Bank-site shop view: a zero saving is not a selling point, so the row is hidden.
    if (this.hasAttribute('data-pk-line-controls')) this.savings.parentElement.hidden = savings <= 0;
    /* Bank: name the free-gift share of the saving, matching the gift values on the lines. */
    if (this.bankDrawer()) {
      const gifts = (view.items || []).reduce((sum, item) => sum + (this.lineRole(item) === 'Gift' ? this.giftValue(item) : 0), 0);
      let note = this.savings.parentElement.querySelector('[data-pm-cart-gift-note]');
      if (gifts > 0 && savings >= gifts) {
        if (!note) {
          note = this.makeText('small', 'pm-cart-drawer__savings-note', '');
          note.dataset.pmCartGiftNote = '';
          this.savings.parentElement.append(note);
        }
        note.textContent = `Includes ${this.formatMoney(gifts)} in free gifts`;
      } else note?.remove();
    }
    const freeShipping = this.hasFreeShipping(cart);
    this.freeShipping.hidden = !freeShipping;
    this.shippingCopy.textContent = freeShipping ? 'Free shipping · Taxes calculated at checkout' :
      cart.requires_shipping === false ? 'Taxes calculated at checkout' : 'Shipping and taxes calculated at checkout.';
    this.renderClubUpsell(cart);
    this.footer.hidden = false;
    this.updateCartCount(hidden.length ? items.reduce((sum, item) => sum + item.quantity, 0) : cart.item_count);
    if (who === 'guest') this.syncJoinCode(cart);
    else if (who === 'account') this.syncJoinCode(cart, true);
    else if (who === 'member') this.syncJoinCode(cart, false, true);

  }

  /* One identity read, owned by snippets/pk-demo-action-lock.liquid. Without it
     (an older head), only a published active entitlement counts as a member. */
  identity() {
    const lock = window.PKDemoActionLock;
    if (lock && typeof lock.identity === 'function') {
      try { return lock.identity(); } catch (_) { return 'resolving'; }
    }
    const auth = window.PK_PUP_CLUB_AUTH || {};
    if (auth.authenticated === true) return auth.activeMember === true ? 'member' : 'account';
    return 'guest';
  }

  enrollRefusal(who) {
    if (who === 'demo') return 'This is a test account. Nothing was added and no membership was started.';
    if (who === 'resolving') return 'Checking your account. Please try again in a moment.';
    return 'You\u2019re signed in, so Pup Club is managed in your account. Nothing was added.';
  }

  /* Test account: shown as an existing member whose checkout is turned off. The
     button looks unavailable and the head lock refuses it on every route, so the
     look and the behaviour agree. Everyone else keeps the normal control. */
  paintLockState(who) {
    if (!this.hasAttribute('data-pk-line-controls')) return;
    const checkout = this.querySelector(`[data-pm-cart-footer] :is(${PmCartDrawer.CHECKOUT})`);
    const note = this.querySelector('[data-pm-cart-lock-note]');
    const locked = who === 'demo';
    if (checkout) {
      checkout.classList.toggle('is-locked', locked);
      if (locked) checkout.setAttribute('aria-disabled', 'true');
      else if (checkout.getAttribute('aria-disabled') === 'true' && this.clubChoiceState !== 'ask') checkout.removeAttribute('aria-disabled');
    }
    if (note) {
      note.hidden = !locked;
      if (locked) note.textContent = 'Test account: checkout is turned off. No real order can be placed.';
    }
  }

  /* A verified signed-in account never enrolls again. A Pup Club line left in its
     cart (from before sign-in, or an old session) is removed once, together with
     the joining code. Products, gifts, other codes and attributes are kept.
     Checkout is held while the write is in flight and released after the cart
     confirms the line is gone. */
  async dropMemberEnrollment(cart, who) {
    const lines = (cart?.items || []).filter((item) => this.isClubLine(item));
    if (!lines.length || this.memberDropPending) return;
    const signature = `${cart.token}:${lines.map((line) => line.key).join(',')}`;
    if (this.memberDropSignature === signature) return;
    this.memberDropSignature = signature;
    this.memberDropPending = true;
    window.PK_CHECKOUT_HOLD = 'Updating your cart\u2026 Please try again in a moment.';
    try {
      const current = await this.fetchCart();
      const stale = (current.items || []).filter((item) => this.isClubLine(item));
      if (!stale.length) { window.PK_CHECKOUT_HOLD = ''; return; }
      if (stale.some((line) => typeof line.key !== 'string' || !line.key)) throw new Error('cart');
      const body = { updates: Object.fromEntries(stale.map((line) => [line.key, 0])) };
      const code = String(this.membership?.joinCode || '').trim().toUpperCase();
      /* An eligible member keeps the joining code (release-5); everyone else loses it. */
      if (who !== 'member' && code && (current.discount_codes || []).some((entry) => String(entry.code || '').toUpperCase() === code)) {
        body.discount = this.joinCodeList(current, false).join(',');
      }
      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(this.cartEditBody(body))
      });
      const next = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(next.items) || next.items.some((item) => this.isClubLine(item))) throw new Error('cart');
      window.PK_CHECKOUT_HOLD = '';
      this.render(next);
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'member-enrollment-removed' } }));
    } catch (_) {
      /* The line stays hidden and checkout stays held; Phoenix's own member rule
         removes it too. A later cart read retries once more. */
      this.memberDropSignature = '';
      window.PK_CHECKOUT_HOLD = 'We couldn\u2019t update your cart. Please refresh the page and try again.';
      if (this.status) this.status.textContent = window.PK_CHECKOUT_HOLD;
    } finally {
      this.memberDropPending = false;
    }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-pm-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Cart, ${count} item${count === 1 ? '' : 's'}`);
      let badge = trigger.querySelector('[data-pm-cart-count]');
      if (!count) {
        badge?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.dataset.pmCartCount = '';
        trigger.append(badge);
      }
      badge.textContent = String(count);
    });
  }
}

/* Checkout control: Phoenix strips name="checkout" from the button it binds, so
   the class and the data-pk-checkout marker are matched too. */
PmCartDrawer.CHECKOUT = '[name="checkout"], [data-pk-checkout], .pm-cart-drawer__checkout';

/* Shopify's /discount/<code> link also keeps the code in a discount_code cookie.
   When the joining code is taken off the cart, that cookie goes too. */
PmCartDrawer.forgetDiscountCookie = (code) => {
  try {
    const match = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
    if (match && decodeURIComponent(match[1]).trim().toUpperCase() === String(code || '').trim().toUpperCase()) {
      document.cookie = 'discount_code=; Max-Age=0; path=/';
    }
  } catch (_) { /* cookies unavailable */ }
};

if (!customElements.get('pm-cart-drawer')) {
  customElements.define('pm-cart-drawer', PmCartDrawer);
}

/* Pup Club film cards ("How Pup Club works" in the drawer and on the Bank cart page).
   Tap swaps in the Shopify-hosted film: 720p on phones, 1080p on desktop. The Join page
   portal script binds the same guard, so only one handler ever runs. */
if (!window.__pkClubFilmBound) {
  window.__pkClubFilmBound = true;
  const pkPlayFilm = (frame) => {
    if (!frame || frame.dataset.playing) return;
    frame.dataset.playing = '1';
    const small = window.matchMedia('(max-width: 767px)').matches;
    const video = document.createElement('video');
    video.src = (small ? frame.dataset.pkFilmMd : frame.dataset.pkFilmHd) || frame.dataset.pkFilmHd || '';
    const img = frame.querySelector('img');
    if (img) video.poster = img.currentSrc || img.src;
    video.controls = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.preload = 'auto';
    video.className = 'pkb-film__video';
    frame.removeAttribute('role'); frame.removeAttribute('tabindex'); frame.removeAttribute('aria-label');
    frame.replaceChildren(video);
    const played = video.play(); if (played && played.catch) played.catch(() => {});
  };
  const filmFrom = (event) => (event.target && event.target.closest ? event.target.closest('[data-pk-film]') : null);
  document.addEventListener('click', (event) => { const frame = filmFrom(event); if (frame && !frame.dataset.playing) { event.preventDefault(); event.stopPropagation(); pkPlayFilm(frame); } }, true);
  document.addEventListener('keydown', (event) => { if (event.key !== 'Enter' && event.key !== ' ') return; const frame = filmFrom(event); if (frame && !frame.dataset.playing) { event.preventDefault(); pkPlayFilm(frame); } });
}
